import { useCallback, useEffect, useMemo, useState } from "react";
import { getWorkspaceSelectionKey } from "./workspaceSelectionUtils";

export default function useTemporarySelection(visibleItems) {
  const [selectedByKey, setSelectedByKey] = useState(() => new Map());

  useEffect(() => {
    setSelectedByKey((current) => {
      let changed = false;
      const next = new Map(current);

      visibleItems.forEach((item) => {
        const key = getWorkspaceSelectionKey(item);
        if (key && next.has(key) && next.get(key) !== item) {
          next.set(key, item);
          changed = true;
        }
      });

      return changed ? next : current;
    });
  }, [visibleItems]);
  const visibleKeys = useMemo(
    () => visibleItems.map(getWorkspaceSelectionKey).filter(Boolean),
    [visibleItems]
  );
  const selectedItems = useMemo(
    () => Array.from(selectedByKey.values()),
    [selectedByKey]
  );
  const selectedVisibleCount = useMemo(
    () => visibleKeys.filter((key) => selectedByKey.has(key)).length,
    [selectedByKey, visibleKeys]
  );
  const allVisibleSelected =
    visibleKeys.length > 0 && selectedVisibleCount === visibleKeys.length;
  const partiallyVisibleSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < visibleKeys.length;

  const isTemporarilySelected = useCallback(
    (item) => selectedByKey.has(getWorkspaceSelectionKey(item)),
    [selectedByKey]
  );

  const toggleOne = useCallback((item) => {
    const key = getWorkspaceSelectionKey(item);
    if (!key) return;

    setSelectedByKey((current) => {
      const next = new Map(current);
      if (next.has(key)) next.delete(key);
      else next.set(key, item);
      return next;
    });
  }, []);

  const toggleVisible = useCallback(() => {
    setSelectedByKey((current) => {
      const next = new Map(current);
      const shouldRemove =
        visibleKeys.length > 0 && visibleKeys.every((key) => next.has(key));

      visibleItems.forEach((item) => {
        const key = getWorkspaceSelectionKey(item);
        if (!key) return;
        if (shouldRemove) next.delete(key);
        else next.set(key, item);
      });

      return next;
    });
  }, [visibleItems, visibleKeys]);

  const clearSelection = useCallback(() => setSelectedByKey(new Map()), []);
  const replaceSelection = useCallback((items) => {
    setSelectedByKey(new Map(
      items
        .map((item) => [getWorkspaceSelectionKey(item), item])
        .filter(([key]) => Boolean(key))
    ));
  }, []);

  return {
    selectedItems,
    selectedCount: selectedItems.length,
    isTemporarilySelected,
    toggleOne,
    toggleVisible,
    clearSelection,
    replaceSelection,
    allVisibleSelected,
    partiallyVisibleSelected,
  };
}
